
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import LienDrawer from "./LienDrawer";
import CreateLien from "./CreateLien";
import { formatCurrency, formatDateLocal, retriveLienFromPractice } from "../../../../lib/script";
import { resolveLatestAmendLien, canCreateAnotherAmendedLien } from "../../../../lib/lienAmended";
import { createSatisfactionLetter } from "../../../../lib/fetchApi";
import { parseApiDateOrDateTime } from "../../../../lib/dateParsing";
import {
    getPracticeIntentNoticeDocuments,
    getLatestPracticeSatisfactionLetters,
    practiceHasLienDrawerContent,
} from "../../../../lib/lienIntentNotices";
import { getLienStageErrorMessages } from "../../../../lib/lienCreationErrors";
import { lienRowHasRecordedStatus } from "../../../../lib/lienRetry";
import {
    formatCreatedOnHover,
    getLienCreatedDate,
    getLienDisplayDate,
    lienDisplayUsesRecordingDate,
} from "../../../../lib/lienDisplayDate";
import { notificationActions } from "../../../../store/notifications";

import classes from './LienTableItem.module.css'

import Button from "../../../common/Button";
import DisplayBoxRight from "../../../fragments/DisplayBoxRight";
import BodySimpleModalV3 from "../../../common/modal/BodySimpleModalV3";

export default function LienTableItem({drawerData, permission}) {
    const dispatch = useDispatch()
    const userToken = useSelector(state => state.user.token)
    const activeCase = useSelector(state => state.case.activeCase)
    const [openedDrwer, setOpenedDrwer] = useState(false)
    const [createLetterLoading, setCreateLetterLoading] = useState(false)
    const lienByType = {
        intent: retriveLienFromPractice(drawerData.liens, "intent"),
        lien: retriveLienFromPractice(drawerData.liens, "lien"),
        amend: resolveLatestAmendLien(drawerData.liens),
        satisfy: retriveLienFromPractice(drawerData.liens, "satisfy")
    }
    const showCreateAmendedButton = canCreateAnotherAmendedLien(drawerData)
    const doNotLienEngaged = Boolean(activeCase?.case?.do_not_lien)
    const hasDrawerContent = practiceHasLienDrawerContent(drawerData)

    function stageShouldShowErrorIcon(type) {
        const lien = lienByType[type]
        if (lien && lienRowHasRecordedStatus(lien)) {
            return false
        }
        const errorMessages = getLienStageErrorMessages(drawerData?.lien_creation_errors, type)
        return errorMessages.length > 0
    }

    function renderStageErrorsIcon(type) {
        if (!stageShouldShowErrorIcon(type)) {
            return null
        }
        const errorMessages = getLienStageErrorMessages(drawerData?.lien_creation_errors, type)

        return (
            <BodySimpleModalV3
                message={<>
                    {errorMessages.map((errorItem, index) => (
                        <div key={`${type}-${index}-${errorItem}`}>{errorItem}</div>
                    ))}
                </>}
            >
                <span className={classes.lien_error_icon}>
                    <i className='ti ti-exclamation-circle'></i>
                </span>
            </BodySimpleModalV3>
        )
    }

    function clickHandler() {
        setOpenedDrwer(prevState => prevState = !prevState)
    }

    function renderLienDateCell(type) {
        const lien = lienByType[type]
        if (!lien) {
            if (type === 'intent') {
                const notices = getPracticeIntentNoticeDocuments(drawerData)
                if (notices.length > 0) {
                    const earliest = notices
                        .map((n) => n.created_at)
                        .filter(Boolean)
                        .sort()[0]
                    if (earliest) {
                        return (
                            <DisplayBoxRight>
                                <span>{formatDateLocal(earliest)}</span>
                            </DisplayBoxRight>
                        )
                    }
                }
            }
            return null
        }
        const displayDate = getLienDisplayDate(lien)
        const hasDate = !!displayDate
        const dateText = hasDate ? formatDateLocal(displayDate) : ''
        const showErrorIcon = stageShouldShowErrorIcon(type)
        const createdHover = lienDisplayUsesRecordingDate(lien)
            ? formatCreatedOnHover(getLienCreatedDate(lien), formatDateLocal)
            : null

        if (!showErrorIcon && !dateText) {
            return <DisplayBoxRight></DisplayBoxRight>
        }

        const dateNode = dateText ? (
            <span title={createdHover || undefined}>{dateText}</span>
        ) : null

        return (
            <DisplayBoxRight>
                <div
                    className={`${classes.lien_cell_content} ${
                        showErrorIcon && !dateText ? classes.lien_cell_content_error_only : ""
                    }`}
                >
                    {showErrorIcon && renderStageErrorsIcon(type)}
                    {dateNode}
                </div>
            </DisplayBoxRight>
        )
    }

    function getSatisfactionLetterDate() {
        let latest = null
        let latestTime = -Infinity
        for (const doc of getLatestPracticeSatisfactionLetters(drawerData)) {
            if (!doc?.created_at) {
                continue
            }
            const time = parseApiDateOrDateTime(doc.created_at)?.getTime()
            if (!Number.isFinite(time) || time < latestTime) {
                continue
            }
            latestTime = time
            latest = doc.created_at
        }
        return latest
    }

    async function createSatisfactionLetterHandler() {
        setCreateLetterLoading(true)
        try {
            const body = {
                case_practice_id: drawerData.id,
            }

            if (lienByType.satisfy?.id) {
                body.case_lien_id = lienByType.satisfy.id
            }

            const update = await createSatisfactionLetter(
                userToken,
                activeCase.case.id,
                body,
                dispatch
            )

            if (update && update.errorMessage) {
                dispatch(notificationActions.setError(update))
            } else {
                dispatch(notificationActions.setError({
                    errorMessage: 'Satisfaction letter has been created.',
                    type: 'success'
                }))
            }
        } finally {
            setCreateLetterLoading(false)
        }
    }

    function renderSatisfactionLetterCell() {
        const letterDate = getSatisfactionLetterDate()
        if (letterDate) {
            return (
                <DisplayBoxRight>
                    <span>{formatDateLocal(letterDate)}</span>
                </DisplayBoxRight>
            )
        }

        if (!drawerData?.location_id) {
            return (
                <BodySimpleModalV3 message={'Practice has no location'} >
                    <div className="text-center">
                        <i className='ti ti-exclamation-circle'></i>
                    </div>
                </BodySimpleModalV3>
            )
        }

        if (doNotLienEngaged && !permission) {
            return (
                <div className={classes.lien_cell_content}>
                    <BodySimpleModalV3 message="Do Not Lien is engaged. Satisfaction letter creation is blocked." widthAuto>
                        <Button btnStyle={'btn_small'} isDisabled={true}>
                            <i className="ti ti-square-plus"></i>Create Satisfaction Letter
                        </Button>
                    </BodySimpleModalV3>
                </div>
            )
        }

        return !permission && (
            <div className={classes.lien_cell_content}>
                <Button btnStyle={'btn_small'} click={createSatisfactionLetterHandler} isLoading={createLetterLoading}>
                    <i className="ti ti-square-plus"></i>Create Satisfaction Letter
                </Button>
            </div>
        )
    }

    // console.log('drawerData', drawerData)
    return <>
        <tr className={classes.lien_item}>
            <td
                onClick={hasDrawerContent ? clickHandler : undefined}
                className={hasDrawerContent ? `${classes.trigger} ${openedDrwer ? classes.active : ''}` : undefined}
            >
                {hasDrawerContent && <i className="ti ti-chevron-right"></i>} {drawerData.practiceDetailed?.name}
            </td>
            <td>{drawerData.chart_number}</td>
            <td>{drawerData.practiceDetailed?.locations.find(elem => elem.id == drawerData.location_id)?.display_name}</td>
            {/* display_name */}
            <td>{drawerData.lien_status}</td>
            <td>
                <DisplayBoxRight>
                    {drawerData.balance ? `$${formatCurrency(drawerData.balance)}` : ''}
                </DisplayBoxRight>
            </td>
            <td>
                <DisplayBoxRight>
                    {drawerData.lien_amount ? `$${formatCurrency(drawerData.lien_amount)}` : ''}
                </DisplayBoxRight>
                
            </td>
            <td className='table-cell-fit'>
                {lienByType.intent
                ? 
                renderLienDateCell("intent")
                // <DateDisplayWithNmuberOfDays date={retriveLienFromPractice(drawerData.liens, "intent").created_at} hasTime={true}/>
                // formatDateLocalWithTime(retriveLienFromPractice(drawerData.liens, "intent").created_at) 
                : 
                <>
                
                {drawerData?.location_id ?     
                    <div className={classes.lien_cell_content}>
                        {renderStageErrorsIcon("intent")}
                        <CreateLien drawerData={drawerData} btnText={'Create Notice'} type="intent" validationSlug='validate-notice-of-intent' permission={permission}/>
                    </div>
                    :                    
                    <BodySimpleModalV3 message={'Practice has no location'} >
                        <div className="text-center">
                            <i className='ti ti-exclamation-circle'></i>

                        </div>
                        
                    </BodySimpleModalV3>
                }
                </>
                }
            </td>
            <td className='table-cell-fit'> 
                {lienByType.lien
                ? 
                renderLienDateCell("lien")
                // <DateDisplayWithNmuberOfDays date={retriveLienFromPractice(drawerData.liens, "lien").created_at} hasTime={true}/>
                // formatDateLocalWithTime(retriveLienFromPractice(drawerData.liens, "lien").created_at) 
                : 
                
                <>
                    {drawerData?.location_id ?     
                        <div className={classes.lien_cell_content}>
                            {renderStageErrorsIcon("lien")}
                            <CreateLien drawerData={drawerData} btnText={'Create Lien'} type="lien" validationSlug='validate-original-lien' permission={permission}/>
                        </div>
                        :                    
                        <BodySimpleModalV3 message={'Practice has no location'} >
                            <div className="text-center">
                                <i className='ti ti-exclamation-circle'></i>

                            </div>
                            
                        </BodySimpleModalV3>
                    }
                </>
                }
            </td>
            <td className='table-cell-fit'>
                {drawerData?.location_id ? (
                    <div className={classes.lien_cell_content}>
                        {lienByType.amend ? renderLienDateCell("amend") : null}
                        {showCreateAmendedButton ? (
                            <>
                                {renderStageErrorsIcon("amend")}
                                <CreateLien
                                    drawerData={drawerData}
                                    btnText={'Create Amended'}
                                    type="amend"
                                    validationSlug='validate-amended-lien'
                                    permission={permission}
                                    iconOnly={!!lienByType.amend}
                                />
                            </>
                        ) : null}
                    </div>
                ) : (
                    <BodySimpleModalV3 message={'Practice has no location'} >
                        <div className="text-center">
                            <i className='ti ti-exclamation-circle'></i>
                        </div>
                    </BodySimpleModalV3>
                )}
            </td>
            <td className='table-cell-fit'>
                {lienByType.satisfy
                ? 
                renderLienDateCell("satisfy")
                // <DateDisplayWithNmuberOfDays date={retriveLienFromPractice(drawerData.liens, "satisfy").created_at} hasTime={true}/>
                // formatDateLocalWithTime(retriveLienFromPractice(drawerData.liens, "satisfy").created_at) 
                : 
                
                <>
                    {drawerData?.location_id ?     
                            <div className={classes.lien_cell_content}>
                                {renderStageErrorsIcon("satisfy")}
                                <CreateLien drawerData={drawerData} btnText={'Satisfy Lien'} type="satisfy" validationSlug='validate-satisfy-lien' permission={permission} />
                            </div>
                        :                    
                        <BodySimpleModalV3 message={'Practice has no location'} >
                            <div className="text-center">
                                <i className='ti ti-exclamation-circle'></i>

                            </div>
                            
                        </BodySimpleModalV3>
                    }
                </>
                }
            </td>
            <td className='table-cell-fit'>
                {renderSatisfactionLetterCell()}
            </td>
        </tr>
        {hasDrawerContent && <>
            {openedDrwer && <tr>
                <td colSpan={11} className="table-cell-no-padding">
                    <LienDrawer data={drawerData.liens} fullData={drawerData} clinic={drawerData.practiceDetailed?.locations.find(elem => elem.id == drawerData.location_id)}/>   
                </td>    
            </tr>}
        </>}
    </>
}